// frontend/packages/business-console/src/pages/Scheduling/CalendarTab.jsx
//
// Bookings list for Scheduling Phase A. Desktop: a table grouped by day with a
// status filter; mobile: cards. Row actions drive the booking lifecycle
// (cancel / reschedule / no-show / complete) through confirm dialogs. No drag
// calendar in Phase A (deferred); this is the readable, actionable list.
//
// There is NO create-booking action here (D22): Phase A bookings are minted by
// the public widget; the Console lists and manages them.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';

import {
  listBookings,
  cancelBooking,
  rescheduleBooking,
  noShowBooking,
  completeBooking,
} from '../../services/schedulingService';
import { Surface, StatusPill, useTokens } from '../../components/primitives';
import {
  statusMeta,
  isActiveBooking,
  formatDay,
  formatTime,
  groupByDay,
  bookingCustomerLabel,
  utcIsoToLocalInput,
  localInputToUtcIso,
} from './helpers';

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'pending_confirmation', label: 'Pending' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'no_show', label: 'No show' },
];

function serverMessage(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

export default function CalendarTab() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Lifecycle action state.
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [rescheduleValue, setRescheduleValue] = useState('');
  const [confirmTarget, setConfirmTarget] = useState(null); // { booking, action }
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query = { limit: 100 };
      if (statusFilter !== 'all') query.status = statusFilter;
      const result = await listBookings(query);
      setPage(result);
    } catch (err) {
      setError(serverMessage(err, "Couldn't load bookings."));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const items = page?.docs || [];
  const groups = groupByDay(items);

  const runCancel = async () => {
    if (!cancelTarget || busy) return;
    setBusy(true);
    setActionError(null);
    try {
      await cancelBooking(cancelTarget._id, {
        reason: cancelReason.trim() || undefined,
      });
      setCancelTarget(null);
      setCancelReason('');
      load();
    } catch (err) {
      setActionError(serverMessage(err, "Couldn't cancel this booking."));
    } finally {
      setBusy(false);
    }
  };

  const runReschedule = async () => {
    if (!rescheduleTarget || busy) return;
    const iso = localInputToUtcIso(rescheduleValue);
    if (!iso) {
      setActionError('Pick a new date and time.');
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      await rescheduleBooking(rescheduleTarget._id, { startUtc: iso });
      setRescheduleTarget(null);
      setRescheduleValue('');
      load();
    } catch (err) {
      setActionError(serverMessage(err, "Couldn't reschedule this booking."));
    } finally {
      setBusy(false);
    }
  };

  const runConfirmAction = async () => {
    if (!confirmTarget || busy) return;
    setBusy(true);
    setActionError(null);
    try {
      if (confirmTarget.action === 'complete') {
        await completeBooking(confirmTarget.booking._id);
      } else if (confirmTarget.action === 'no-show') {
        await noShowBooking(confirmTarget.booking._id);
      }
      setConfirmTarget(null);
      load();
    } catch (err) {
      setActionError(serverMessage(err, "Couldn't update this booking."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}
      >
        {STATUS_FILTERS.map((f) => (
          <Chip
            key={f.key}
            label={f.label}
            size="small"
            clickable
            color={statusFilter === f.key ? 'primary' : 'default'}
            variant={statusFilter === f.key ? 'filled' : 'outlined'}
            onClick={() => setStatusFilter(f.key)}
          />
        ))}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Refresh">
          <span>
            <IconButton
              size="small"
              onClick={load}
              disabled={loading}
              aria-label="Refresh bookings"
            >
              <RefreshRoundedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {error ? (
        <Alert severity="error" onClose={() => load()}>
          {error}
        </Alert>
      ) : null}
      {actionError ? (
        <Alert severity="error" onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <EmptyState />
      ) : (
        groups.map((group) => (
          <Box key={group.dayKey}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                letterSpacing: '.5px',
                textTransform: 'uppercase',
                color: 'text.secondary',
                display: 'block',
                mb: 0.75,
              }}
            >
              {group.dayLabel}
            </Typography>
            {isMobile ? (
              <BookingCards
                items={group.items}
                onCancel={setCancelTarget}
                onReschedule={(b) => {
                  setRescheduleTarget(b);
                  setRescheduleValue(utcIsoToLocalInput(b.startUtc));
                }}
                onConfirmAction={setConfirmTarget}
              />
            ) : (
              <BookingTable
                items={group.items}
                onCancel={setCancelTarget}
                onReschedule={(b) => {
                  setRescheduleTarget(b);
                  setRescheduleValue(utcIsoToLocalInput(b.startUtc));
                }}
                onConfirmAction={setConfirmTarget}
              />
            )}
          </Box>
        ))
      )}

      {/* Cancel dialog */}
      <Dialog
        open={Boolean(cancelTarget)}
        onClose={() => (busy ? null : setCancelTarget(null))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Cancel this booking?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            The customer will be notified their booking is cancelled. This can
            be recovered by support.
          </DialogContentText>
          <TextField
            fullWidth
            size="small"
            label="Reason (optional)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            disabled={busy}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCancelTarget(null)} disabled={busy}>
            Keep booking
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={runCancel}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={14} /> : null}
          >
            Cancel booking
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reschedule dialog */}
      <Dialog
        open={Boolean(rescheduleTarget)}
        onClose={() => (busy ? null : setRescheduleTarget(null))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Reschedule booking</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Pick a new start time. The customer keeps the same service and staff
            member. The server re-checks availability before saving.
          </DialogContentText>
          <TextField
            fullWidth
            size="small"
            type="datetime-local"
            label="New start"
            value={rescheduleValue}
            onChange={(e) => setRescheduleValue(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={busy}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRescheduleTarget(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={runReschedule}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={14} /> : null}
          >
            Save new time
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete / no-show confirm dialog */}
      <Dialog
        open={Boolean(confirmTarget)}
        onClose={() => (busy ? null : setConfirmTarget(null))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {confirmTarget?.action === 'complete'
            ? 'Mark as completed?'
            : 'Mark as no show?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmTarget?.action === 'complete'
              ? 'This records the appointment as completed.'
              : 'This records that the customer did not show up.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmTarget(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={runConfirmAction}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={14} /> : null}
          >
            {confirmTarget?.action === 'complete'
              ? 'Mark completed'
              : 'Mark no show'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function RowActions({ booking, onCancel, onReschedule, onConfirmAction }) {
  const [anchor, setAnchor] = useState(null);
  if (!isActiveBooking(booking.status)) return null;
  const close = () => setAnchor(null);
  return (
    <>
      <Tooltip title="Actions">
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setAnchor(e.currentTarget);
          }}
          aria-label="Booking actions"
        >
          <MoreVertRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
        <MenuItem
          onClick={() => {
            close();
            onReschedule(booking);
          }}
        >
          Reschedule
        </MenuItem>
        <MenuItem
          onClick={() => {
            close();
            onConfirmAction({ booking, action: 'complete' });
          }}
        >
          Mark completed
        </MenuItem>
        <MenuItem
          onClick={() => {
            close();
            onConfirmAction({ booking, action: 'no-show' });
          }}
        >
          Mark no show
        </MenuItem>
        <MenuItem
          sx={{ color: 'error.main' }}
          onClick={() => {
            close();
            onCancel(booking);
          }}
        >
          Cancel booking
        </MenuItem>
      </Menu>
    </>
  );
}

function BookingTable({ items, onCancel, onReschedule, onConfirmAction }) {
  const t = useTokens();
  return (
    <TableContainer component={Surface} sx={{ overflow: 'hidden' }}>
      <Table
        size="small"
        sx={{
          '& th': {
            fontWeight: 800,
            fontSize: 10,
            letterSpacing: '.4px',
            textTransform: 'uppercase',
            color: t.faint,
            bgcolor: t.tableHead,
            borderBottom: `1px solid ${t.hairline}`,
          },
          '& td': { borderBottom: `1px solid ${t.hairline}`, color: t.text },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 100 }}>Time</TableCell>
            <TableCell>Customer</TableCell>
            <TableCell>Service</TableCell>
            <TableCell sx={{ width: 120 }}>Status</TableCell>
            <TableCell sx={{ width: 44 }} aria-label="Actions" />
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((b) => {
            const meta = statusMeta(b.status);
            return (
              <TableRow key={b._id} hover>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {formatTime(b.startUtc)}
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                    {bookingCustomerLabel(b)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ color: t.muted }}>
                  {b.appointmentTypeName || b.serviceName || '-'}
                </TableCell>
                <TableCell>
                  <StatusPill size="sm" kind={meta.kind} label={meta.label} />
                </TableCell>
                <TableCell padding="none" align="center">
                  <RowActions
                    booking={b}
                    onCancel={onCancel}
                    onReschedule={onReschedule}
                    onConfirmAction={onConfirmAction}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function BookingCards({ items, onCancel, onReschedule, onConfirmAction }) {
  return (
    <Stack spacing={1.5}>
      {items.map((b) => {
        const meta = statusMeta(b.status);
        return (
          <Surface key={b._id} sx={{ p: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 1,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700 }}>
                  {bookingCustomerLabel(b)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {formatDay(b.startUtc)} at {formatTime(b.startUtc)}
                </Typography>
                {b.appointmentTypeName || b.serviceName ? (
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', display: 'block' }}
                  >
                    {b.appointmentTypeName || b.serviceName}
                  </Typography>
                ) : null}
              </Box>
              <Stack alignItems="flex-end" spacing={0.5}>
                <StatusPill size="sm" kind={meta.kind} label={meta.label} />
                <RowActions
                  booking={b}
                  onCancel={onCancel}
                  onReschedule={onReschedule}
                  onConfirmAction={onConfirmAction}
                />
              </Stack>
            </Box>
          </Surface>
        );
      })}
    </Stack>
  );
}

function EmptyState() {
  return (
    <Surface
      sx={{
        borderStyle: 'dashed',
        py: 6,
        px: 3,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <EventBusyOutlinedIcon
        sx={{ fontSize: 44, color: 'text.secondary', opacity: 0.6 }}
      />
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        No bookings yet
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: 'text.secondary', maxWidth: 380 }}
      >
        When customers book from your website, their appointments show up here.
      </Typography>
    </Surface>
  );
}
